import React from "react";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import AnalyticsIcon from "@mui/icons-material/Analytics";

import * as Api from "../../Api";
import * as NavigationUtils from "./NavigationUtils";
import * as FleetSelectors from "store/Fleet/Fleet.selector";

import store from "store";
import {
	FILE_LIST_ITEM_TRANSITION_TIME,
	FILE_TYPES,
	FOLDER_GLOBAL,
	FOLDER_GLOBAL_DISPLAY_NAME,
} from "lib/consts/FilesConstants";
import { FileTree, File as StorageFile } from "lib/types/FileTypes";
import { scrollToElement } from "./Utils";
import { Route } from "./NavigationUtils";
import { findGroupName } from "./GroupUtils";
import { getFleetDisplayName } from "./FormatUtils";

export interface UploadFormState {
	progress: number;
	totalDrivers?: number;
	complete: boolean;
	fileName: string;
	errors: any[];
	loading: boolean;
	limitExceeded?: boolean;
	invalidFile: boolean;
	noDrivers?: boolean;
}

export const updateState = (
	newData: Partial<UploadFormState>,
	setState: React.Dispatch<React.SetStateAction<UploadFormState>>,
) =>
	setState((prevState: UploadFormState) => ({
		...prevState,
		...newData,
	}));

export const beginUpload = (
	state: UploadFormState,
	interval: React.MutableRefObject<number | null>,
	onComplete: () => void,
	setState: React.Dispatch<React.SetStateAction<UploadFormState>>,
	getState: () => UploadFormState,
	limitExceeded?: boolean,
	noDrivers?: boolean,
) => {
	if (state.errors.length) {
		return;
	}

	interval.current = window.setInterval(
		() =>
			onUploadProgress(
				getState().progress + 10,
				interval,
				onComplete,
				setState,
				limitExceeded,
				noDrivers,
			),
		100,
	);
};

const onUploadProgress = (
	progress: number,
	interval: React.MutableRefObject<number | null>,
	onComplete: () => void,
	setState: React.Dispatch<React.SetStateAction<UploadFormState>>,
	limitExceeded?: boolean,
	noDrivers?: boolean,
) => {
	if (progress >= 100 || limitExceeded || noDrivers) {
		if (interval.current) {
			clearInterval(interval.current);
		}

		interval.current = null;
		return onComplete();
	}

	updateState({ progress }, setState);
};

export const resetForm = (
	interval: React.MutableRefObject<number | null>,
	setState: React.Dispatch<React.SetStateAction<UploadFormState>>,
) => {
	if (interval.current) {
		clearInterval(interval.current);
		interval.current = null;
	}

	updateState(
		{
			fileName: "",
			progress: 0,
			complete: false,
		},
		setState,
	);
};

export const getFileIconFromFileType = (fileType: string) => {
	let labelIcon;

	if (fileType !== undefined) {
		const baseType = fileType.split("/")[0];
		if (baseType === "image") {
			return ImageIcon;
		}
	}

	if (fileType === FILE_TYPES.PDF) {
		labelIcon = PictureAsPdfIcon;
	} else if (
		fileType === FILE_TYPES.CSV ||
		fileType === FILE_TYPES.EXCEL ||
		fileType === FILE_TYPES.XLSX
	) {
		labelIcon = TableViewIcon;
	} else if (
		fileType === FILE_TYPES.PLAIN ||
		fileType === FILE_TYPES.JSON ||
		fileType === FILE_TYPES.DOCX ||
		fileType === FILE_TYPES.DOC
	) {
		labelIcon = DescriptionIcon;
	} else if (fileType === FILE_TYPES.ZIP) {
		labelIcon = FolderZipIcon;
	} else if (fileType === FILE_TYPES.RTF) {
		labelIcon = TextSnippetIcon;
	} else if (fileType === FILE_TYPES.HTML || fileType === FILE_TYPES.MD) {
		labelIcon = IntegrationInstructionsIcon;
	} else if (
		fileType === FILE_TYPES.PRESENTATION ||
		fileType === FILE_TYPES.POWERPOINT
	) {
		labelIcon = AnalyticsIcon;
	} else {
		labelIcon = InsertDriveFileIcon;
	}

	return labelIcon;
};

export const fetchFiles = async (
	fleetId: string,
	groupId?: string,
	groupHierarchy?: string,
) => {
	try {
		const res = await Api.fetchFiles.submit({
			params: { fleetId },
			query: { groupHierarchy, groupId },
		});
		return res.data;
	} catch (err) {
		const publicErrMsg = err?.response?.data?.message;
		throw new Error(
			`Error when fetching files: ${publicErrMsg || err.message}`,
		);
	}
};

export const fetchFileSecureURL = async (
	fleetId: string,
	filePath: string,
	groupId?: string,
	friendlyName?: string,
) => {
	try {
		const fileId = getFileIdFromPath(filePath);

		const res = await Api.fetchFileSecureUrl.submit({
			params: { fleetId },
			query: { fileId, friendlyName, groupId },
		});
		const { secureUrl } = res.data;
		return secureUrl;
	} catch (err) {
		const publicErrMsg = err?.response?.data?.message;
		throw new Error(
			`Error fetching secure URL: ${publicErrMsg || err.message}`,
		);
	}
};

export const uploadFilesToStorage = async (
	fleetId: string,
	fileData: File,
	folderPath: string,
	friendlyName: string,
	groupId?: string,
) => {
	try {
		// Support for special characters in file names
		const encodedFileName = btoa(unescape(encodeURIComponent(fileData.name)));
		const fileExtension = fileData.name
			.slice(fileData.name.lastIndexOf("."))
			.toLowerCase();
		let contentType = fileData.type;

		if (fileData.type === FILE_TYPES.JSON) {
			const fileContent = await fileData.text();
			try {
				JSON.parse(fileContent);
			} catch (err) {
				throw new Error("Invalid JSON file.");
			}
		} else if (fileExtension === FILE_TYPES.MD_EXTENSION) {
			contentType = FILE_TYPES.MD;
		}

		const res = await Api.postUploadFiles.submit({
			params: { fleetId },
			query: { groupHierarchy: folderPath, friendlyName, groupId },
			body: fileData as any,
			headers: {
				"x-original-filename": `${encodedFileName}`,
				"Content-Type": `${contentType}`,
			},
		});
		return res;
	} catch (err) {
		const publicErrMsg = err?.response?.data?.message;
		throw new Error(
			`Error when uploading file: ${publicErrMsg || err.message}`,
		);
	}
};

export const setNewFriendlyName = async (
	fleetId: string,
	filePath: string,
	newFileName: string,
	groupId?: string,
) => {
	try {
		const fileId = getFileIdFromPath(filePath);

		const res = await Api.postRenameFile.submit({
			params: { fleetId, fileId },
			query: { newFileName, groupId },
		});
		return res;
	} catch (err) {
		const publicErrMsg = err?.response?.data?.message;
		throw new Error(`Error when renaming file: ${publicErrMsg || err.message}`);
	}
};

export const deleteFileByName = async (
	fleetId: string,
	filePath: string,
	groupId?: string,
) => {
	try {
		const fileId = getFileIdFromPath(filePath);

		const res = await Api.postDeleteFile.submit({
			params: { fleetId, fileId },
			query: { groupId },
		});
		return res;
	} catch (err) {
		const publicErrMsg = err?.response?.data?.message;
		throw new Error(`Error deleting file: ${publicErrMsg || err.message}`);
	}
};

export const setFriendlyNameLocally = (
	fileId: string,
	newName: string,
	fileTree: FileTree,
) => {
	for (const [key, value] of Object.entries(fileTree.content)) {
		if ((value as FileTree).content) {
			setFriendlyNameLocally(fileId, newName, value as FileTree);
		} else if (value.path === fileId) {
			(value as StorageFile).friendlyName = newName;
		}
	}
	return fileTree;
};

export const deleteFileLocally = (
	deletedFileName: string,
	fileTree: FileTree,
) => {
	for (const [key, value] of Object.entries(fileTree.content)) {
		if ((value as FileTree).content) {
			deleteFileLocally(deletedFileName, value as FileTree);
		} else if (value.path === deletedFileName) {
			delete fileTree.content[key];
		}
	}
	return fileTree;
};

export const scrollFileItemIntoView = async (
	fileId: string,
	groupId?: string,
) => {
	const openFolder = async (folderId: string) => {
		const folderElement = document.querySelectorAll(
			`[id^=":r"][id$="-${folderId}"]`,
		)[0];
		if (!folderElement) {
			return false;
		}
		const isFolderOpen = folderElement.getAttribute("aria-expanded") === "true";
		if (!isFolderOpen) {
			try {
				(folderElement.children[0] as HTMLElement).click();
			} catch (error) {
				console.warn("Wasn't able to open folder", error);
			}
		}
		return true;
	};

	let isFolderOpen = false;
	let openableFolderStructure = "";

	if (groupId) {
		const startingIndex = fileId.indexOf(groupId);
		openableFolderStructure = fileId.substring(startingIndex);
	} else {
		openableFolderStructure = fileId;
	}

	for await (const folderName of openableFolderStructure.split("/")) {
		isFolderOpen = await openFolder(folderName);
		if (!isFolderOpen) {
			break;
		}
		await new Promise((resolve) => {
			setTimeout(resolve, FILE_LIST_ITEM_TRANSITION_TIME);
		});
	}

	const fileElement = document.getElementById(`*:-${fileId}`);
	if (fileElement) {
		scrollToElement(fileElement, 10);
		(fileElement.children[0] as HTMLElement).click();
	}
};

export const runFileManagerEnabledCheck = (route: Route, fleetId: string) => {
	const state = store?.getState();
	const emulatedFleet = FleetSelectors.getFleetById(state, fleetId);

	if (emulatedFleet) {
		NavigationUtils.runMiddlewareForRoute(route, emulatedFleet);
	}
};

export const getFileManagerFolderName = (fleetId: string, name: string) => {
	let folderName = findGroupName(fleetId, name, false).toString();

	if (folderName === fleetId) {
		folderName = getFleetDisplayName(fleetId, false).toString();
	}

	if (folderName === FOLDER_GLOBAL) {
		folderName = FOLDER_GLOBAL_DISPLAY_NAME;
	}

	return folderName;
};

const getFileIdFromPath = (filePath: string) => {
	const isGlobalFile = filePath.startsWith(FOLDER_GLOBAL);
	return !isGlobalFile
		? btoa(unescape(encodeURIComponent(filePath.split("/").slice(1).join("/"))))
		: btoa(unescape(encodeURIComponent(filePath)));
};

interface FileManagerContextState {
	selectedFolderPath?: string;
	error?: Error | null;
	setSelectedFolderPath: (path: string) => void;
	setError: (error: any) => void;
}

export const FileManagerContext = React.createContext<FileManagerContextState>({
	selectedFolderPath: "",
	error: null,
	setSelectedFolderPath: () => {},
	setError: () => {},
});
